import { Spacing } from '@persona-id/design-tokens';
import {
  PlusIcon as IconPlus,
  WorkflowsIcon as IconWorkflows,
} from '@persona-id/design-tokens/icons';
import { Dispatch, FC, SetStateAction, useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom-v5-compat';
import { COLORS } from 'shared/styles/variables';
import styled from 'styled-components';

import DisabledProductFeature from 'dashboard/components/DisabledProductFeature';
import FeatureFlagged from 'dashboard/components/FeatureFlagged';
import InactiveEnvironmentBanner from 'dashboard/components/InactiveEnvironmentBanner';
import { SelectOptionType } from 'dashboard/components/JsonSchemaSelect/SelectOption';
import ModalWrapper from 'dashboard/components/ModalWrapper';
import Pagination from 'dashboard/components/Pagination';
import WorkflowActivateModal from 'dashboard/components/WorkflowActivateModal';
import WorkflowDeactivateModal from 'dashboard/components/WorkflowDeactivateModal';
import ConfirmationModal from 'dashboard/components-lib/ConfirmationModal';
import LoadingIndicator from 'dashboard/components-lib/LoadingIndicator';
import { Container, Header, Section } from 'dashboard/layouts/IndexViewLayoutV2';
import { DEFAULT_PAGE_SIZE, MIN_PAGE_SIZE } from 'dashboard/lib/constants/pagination';
import { TableState, UseTableStateCallbacksReturn } from 'dashboard/lib/hooks/useTableState';
import { MessageType } from 'dashboard/lib/interfaces/Message';
import { SortParams } from 'dashboard/lib/interfaces/SortParams';
import { Environment } from 'dashboard/models/environment';
import { TransactionType } from 'dashboard/models/transaction-type';
import { Trigger, TriggerType } from 'dashboard/models/trigger';
import { Workflow, WorkflowSchema, WorkflowStatus } from 'dashboard/models/workflow';
import { WorkflowBlock } from 'dashboard/models/workflow-block';
import { WorkflowDeployment } from 'dashboard/models/workflow-deployment';
import { WorkflowModule, WorkflowModulePayload } from 'dashboard/models/workflow-module';
import { WorkflowVersion } from 'dashboard/models/workflow-version';
import { WorkflowsFilterParams } from 'dashboard/redux/actions/workflows';
import { useCreateWorkflowModuleMutation } from 'dashboard/redux/api/workflow-module';
import { TriggerParams } from 'dashboard/redux/thunk-actions/workflow';
import { WorkflowModulesFilterParams } from 'dashboard/routes/DashboardWorkflowsRoute/DashboardWorkflowsRoute';
import CreateModuleModal from 'dashboard/views/DashboardWorkflowsView/CreateModuleModal';
import CreateWorkflowModal from 'dashboard/views/DashboardWorkflowsView/CreateWorkflowModal';
import ModulesViewConfigurationBar from 'dashboard/views/DashboardWorkflowsView/DashboardModulesViewConfigurationBar';
import WorkflowsViewConfigurationBar from 'dashboard/views/DashboardWorkflowsView/DashboardWorkflowsViewConfigurationBar';
import ModuleCards from 'dashboard/views/DashboardWorkflowsView/ModuleCards';
import WorkflowCard from 'dashboard/views/DashboardWorkflowsView/WorkflowCard';
import WorkflowModuleModals from 'dashboard/views/DashboardWorkflowsView/WorkflowModuleModals';
import Button from 'design-system/components/Button';
import Card from 'design-system/components/Card';
import Input from 'design-system/components/Input';
import Spacer from 'design-system/components/Spacer';
import { Tab } from 'design-system/components/TabBar';
import TabBar from 'design-system/components/TabBar/TabBar';
import { Paragraph } from 'design-system/components/Text';
import NullStateWorkflows from 'images/dashboard/i16-workflow.svg';
import { EventName } from 'lib/constants/events';
import usePermission from 'lib/hooks/usePermission';
import useRTKErrorHandler from 'lib/hooks/useRTKErrorHandler';
import useToggle from 'lib/hooks/useToggle';
import { PaginationParams } from 'lib/interfaces/api';
import { Permission } from 'lib/permissions';

const CenteredNullView = styled.div`
  display: flex;
  width: 100%;
  min-height: 200px;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  background: ${COLORS.white};
  padding: 96px;
`;

const PaginationControlsCard = styled(Card)`
  padding: ${Spacing[2]};
  margin-top: ${Spacing[4]};
`;

const PaddedTabBar = styled(TabBar)`
  margin-top: ${Spacing[7]};
  margin-left: ${Spacing[7]};
  margin-right: ${Spacing[7]};
`;

type Tab = 'workflows' | 'modules';

interface DashboardWorkflowsViewProps {
  currentTab: Tab;
  environments: Environment[];
  handleWorkflowOmniFilterUpdate: (nameFilter: string) => void;
  handleWorkflowPageLinkClick: (pageNumber: number) => void;
  handleWorkflowPageSizeClick: (pageSize: number) => void;
  handleWorkflowSortUpdate: (newSortParams: SortParams) => void;
  handleWorkflowStatusFilterUpdate: (status: WorkflowStatus[]) => void;
  handleWorkflowTriggerFilterUpdate: (workflowsTriggerTypess: TriggerType[]) => void;
  handleEventNameFilterUpdate: (eventNames: EventName[]) => void;
  hasWorkflowsBasic: boolean;
  hasWorkflowsEnhanced: boolean;
  hasWorkflowsFree: boolean;
  isLoading: boolean;
  isLoadingModules: boolean;
  isLoadingSchema: boolean;
  onArchiveWorkflow: (workflow: Workflow) => Promise<void>;
  onCloneWorkflow: (workflow: Workflow, name: string | null) => Promise<void>;
  onCreateWorkflow: (name: string, description: string, trigger: TriggerParams) => Promise<void>;
  onPauseWorkflow: (workflow: Workflow) => Promise<void>;
  onRunWorkflow: (workflow: Workflow) => Promise<void>;
  onSelectTab: (tab: Tab) => void;
  pageSize: number;
  paginationParams: PaginationParams;
  referencedWorkflowFilterOptions: SelectOptionType[] | undefined;
  transactionTypes: TransactionType[];
  workflowSchema: WorkflowSchema;
  workflowsFilterParams: WorkflowsFilterParams;
  workflowsSortParams: SortParams;
  workflowModulesTableState: TableState<WorkflowModulesFilterParams>;
  workflowModulesTableCallbacks: UseTableStateCallbacksReturn;
  workflowModulePaginationParams: PaginationParams | undefined;
  workflowModulesWithRelationships: {
    workflowModule: WorkflowModule;
    workflowBlock: WorkflowBlock;
  }[];
  workflowsWithRelationships: {
    workflow: Workflow;
    workflowVersion: WorkflowVersion;
    workflowDeployment?: WorkflowDeployment;
    trigger: Trigger;
  }[];
}

const DashboardWorkflowsView: FC<DashboardWorkflowsViewProps> = ({
  currentTab,
  environments,
  handleWorkflowOmniFilterUpdate,
  handleWorkflowPageLinkClick,
  handleWorkflowPageSizeClick,
  handleWorkflowSortUpdate,
  handleWorkflowStatusFilterUpdate,
  handleWorkflowTriggerFilterUpdate,
  handleEventNameFilterUpdate,
  hasWorkflowsBasic,
  hasWorkflowsEnhanced,
  hasWorkflowsFree,
  isLoading,
  isLoadingModules,
  isLoadingSchema,
  onArchiveWorkflow,
  onCreateWorkflow,
  onCloneWorkflow,
  onPauseWorkflow,
  onRunWorkflow,
  onSelectTab,
  pageSize,
  paginationParams,
  referencedWorkflowFilterOptions,
  transactionTypes,
  workflowSchema,
  workflowsFilterParams,
  workflowsSortParams,
  workflowModulesTableState,
  workflowModulesTableCallbacks,
  workflowModulePaginationParams,
  workflowModulesWithRelationships,
  workflowsWithRelationships,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [
    createWorkflowModal,
    { enable: showCreateWorkflowModal, disable: hideCreateWorkflowModal },
  ] = useToggle(false);
  const [createModuleModal, { enable: showCreateModuleModal, disable: hideCreateModuleModal }] =
    useToggle(false);

  const hasCreatePermission = usePermission(Permission.WorkflowCreate, { global: true });

  // Deal route on the top-level
  const location = useLocation();
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const openCreateWorkflowModalParam = queryParams.get('openCreateWorkflowModal');
    const openCreateModuleModalParam = queryParams.get('openCreateModuleModal');

    if (openCreateWorkflowModalParam === 'true' && hasCreatePermission) {
      showCreateWorkflowModal();
    }
    if (openCreateModuleModalParam === 'true' && hasCreatePermission) {
      showCreateModuleModal();
    }
  }, [location, hasCreatePermission]);

  const [workflowToArchive, setWorkflowToArchive] = useState<Workflow | null>(null);
  const closeArchiveWorkflowVersionModal = useCallback(() => setWorkflowToArchive(null), []);
  const confirmArchiveWorkflowVersion = useCallback(async () => {
    if (workflowToArchive) {
      await onArchiveWorkflow(workflowToArchive);
      setWorkflowToArchive(null);
    }
  }, [onArchiveWorkflow, workflowToArchive]);

  const [clonedWorkflowName, setClonedWorkflowName] = useState<string | null>(null);
  const [workflowToClone, setWorkflowToClone] = useState<Workflow | null>(null);
  const closeCloneWorkflowModal = useCallback(() => {
    setWorkflowToClone(null);
    setClonedWorkflowName(null);
  }, []);
  const cloneWorkflow = useCallback(async () => {
    if (workflowToClone) {
      await onCloneWorkflow(workflowToClone, clonedWorkflowName);
      setWorkflowToClone(null);
      setClonedWorkflowName(null);
    }
  }, [onCloneWorkflow, workflowToClone, clonedWorkflowName]);

  const [workflowToDeactivate, setWorkflowToDeactivate] = useState<Workflow | null>(null);
  const closeDeactivateWorkflowVersionModal = useCallback(() => setWorkflowToDeactivate(null), []);
  const confirmDeactivateWorkflowVersion = useCallback(async () => {
    if (workflowToDeactivate) {
      await onPauseWorkflow(workflowToDeactivate);
      setWorkflowToDeactivate(null);
    }
  }, [onPauseWorkflow, workflowToDeactivate]);

  const [workflowToActivate, setWorkflowToActivate] = useState<Workflow | null>(null);
  const closeActivateWorkflowVersionModal = useCallback(() => setWorkflowToActivate(null), []);
  const confirmActivateWorkflowVersion = useCallback(async () => {
    if (workflowToActivate) {
      await onRunWorkflow(workflowToActivate);
      setWorkflowToActivate(null);
    }
  }, [onRunWorkflow, workflowToActivate]);

  const [createModule, { error: createModuleError }] = useCreateWorkflowModuleMutation();
  const handleCreateModule = useCallback(
    async (data: WorkflowModulePayload) => {
      createModule(data).then((res) => {
        if ('data' in res) {
          const {
            data: { data: newModule },
          } = res;

          const workflowBlockId = newModule.relationships['latest-draft-block'].data?.id;

          navigate(`/dashboard/workflows/modules/${newModule.id}/${workflowBlockId}`);
        }
      });
    },
    [createModule, dispatch],
  );

  useRTKErrorHandler({
    error: createModuleError,
    onError: (error) => ({
      message: (error?.errors?.at(0)?.details as string) || error.message,
      level: MessageType.Failure,
    }),
  });

  const [moduleToActivate, setModuleToActivate] = useState<WorkflowModule | null>(null);
  const [moduleToArchive, setModuleToArchive] = useState<WorkflowModule | null>(null);
  const [moduleToDeactivate, setModuleToDeactivate] = useState<WorkflowModule | null>(null);

  if ([hasWorkflowsFree, hasWorkflowsBasic, hasWorkflowsEnhanced].filter(Boolean).length === 0) {
    return (
      <DisabledProductFeature
        title='Workflows'
        prompt='Automate manual decisions and actions.'
        helpLink={
          <a
            href='https://support.withpersona.com/hc/en-us/articles/360041047533-Workflows'
            target='_blank'
            title='Learn more about Workflows (Opens in a new window)'
            rel='noreferrer'
          >
            Learn more about Workflows
          </a>
        }
        icon={<IconWorkflows />}
        popupMessage='Your current plan does not have access to this feature.'
      />
    );
  }

  return (
    <>
      <ModalWrapper isOpen={createModuleModal} handleCloseModal={hideCreateModuleModal}>
        <CreateModuleModal handleClose={hideCreateModuleModal} onSubmit={handleCreateModule} />
      </ModalWrapper>
      <WorkflowModuleModals
        moduleToActivate={moduleToActivate}
        moduleToArchive={moduleToArchive}
        moduleToDeactivate={moduleToDeactivate}
        setModuleToActivate={setModuleToActivate}
        setModuleToArchive={setModuleToArchive}
        setModuleToDeactivate={setModuleToDeactivate}
      />
      <ModalWrapper
        isOpen={workflowToArchive != null}
        handleCloseModal={closeArchiveWorkflowVersionModal}
      >
        <ConfirmationModal
          cancelAction={closeArchiveWorkflowVersionModal}
          confirmAction={confirmArchiveWorkflowVersion}
          header='Archive this workflow?'
          confirmActionText='Archive'
        >
          <Paragraph>
            <strong>
              This action is permanent and can’t be undone. Please ensure that this workflow is not
              in use before continuing.
            </strong>
          </Paragraph>
        </ConfirmationModal>
      </ModalWrapper>
      <ModalWrapper isOpen={workflowToClone != null} handleCloseModal={closeCloneWorkflowModal}>
        <ConfirmationModal
          cancelAction={closeCloneWorkflowModal}
          confirmAction={cloneWorkflow}
          header='Make a copy of this workflow?'
          confirmActionText='Make a copy'
        >
          <Paragraph>
            This will make a new draft copy of <strong>{workflowToClone?.attributes?.name}</strong>{' '}
            workflow&apos;s latest version.
          </Paragraph>
          <Spacer height={3} />
          <Paragraph weight='bold' color='primary'>
            Name
          </Paragraph>
          <Spacer height={1} />
          <Input
            value={clonedWorkflowName ?? `Copy of ${workflowToClone?.attributes?.name}`}
            onChange={(e) => {
              setClonedWorkflowName(e.target.value);
            }}
          />
        </ConfirmationModal>
      </ModalWrapper>
      <ModalWrapper
        isOpen={workflowToDeactivate != null}
        handleCloseModal={closeDeactivateWorkflowVersionModal}
      >
        <WorkflowDeactivateModal
          workflowName={workflowToDeactivate?.attributes?.name}
          handleClose={closeDeactivateWorkflowVersionModal}
          handleConfirm={confirmDeactivateWorkflowVersion}
        />
      </ModalWrapper>
      <ModalWrapper
        isOpen={workflowToActivate != null}
        handleCloseModal={closeActivateWorkflowVersionModal}
      >
        <WorkflowActivateModal
          workflowName={workflowToActivate?.attributes?.name}
          handleClose={closeActivateWorkflowVersionModal}
          handleConfirm={confirmActivateWorkflowVersion}
          activeEnvironments={
            workflowToActivate
              ? environments.filter((env) =>
                  workflowToActivate.relationships.environments.data
                    .map((env) => env.id)
                    .includes(env.id),
                )
              : []
          }
        />
      </ModalWrapper>
      <ModalWrapper isOpen={createWorkflowModal} handleCloseModal={hideCreateWorkflowModal}>
        <CreateWorkflowModal
          handleClose={hideCreateWorkflowModal}
          hasWorkflowsEnhanced={hasWorkflowsEnhanced}
          onSubmit={onCreateWorkflow}
          transactionTypes={transactionTypes}
        />
      </ModalWrapper>
      <InactiveEnvironmentBanner />
      {currentTab === 'modules' && (
        <Header title='Workflow Modules'>
          {hasCreatePermission && (
            <Button
              id='dashboard-module-view__create'
              onClick={showCreateModuleModal}
              width='auto'
              prefixIcon={<IconPlus />}
            >
              Create module
            </Button>
          )}
        </Header>
      )}
      {currentTab === 'workflows' && (
        <Header title='Workflows'>
          {hasCreatePermission && (
            <Button
              id='dashboard-workflows-view__create'
              onClick={showCreateWorkflowModal}
              width='auto'
              prefixIcon={<IconPlus />}
            >
              Create workflow
            </Button>
          )}
        </Header>
      )}
      <FeatureFlagged featureFlag='global:enable-workflow-modules'>
        <PaddedTabBar>
          <Tab
            id='dashboard-workflows-view__tab-workflows'
            selected={currentTab === 'workflows'}
            onClick={() => onSelectTab('workflows')}
          >
            Workflows
          </Tab>
          <Tab
            id='dashboard-workflows-view__tab-modules'
            selected={currentTab === 'modules'}
            onClick={() => onSelectTab('modules')}
          >
            Modules
          </Tab>
        </PaddedTabBar>
      </FeatureFlagged>
      <Container>
        <Section>
          {currentTab === 'workflows' && (
            <>
              <WorkflowsViewConfigurationBar
                workflowsFilterParams={workflowsFilterParams}
                workflowsSortParams={workflowsSortParams}
                handleOmniFilterUpdate={handleWorkflowOmniFilterUpdate}
                handleStatusFilterUpdate={handleWorkflowStatusFilterUpdate}
                handleTriggerFilterUpdate={handleWorkflowTriggerFilterUpdate}
                handleSortUpdate={handleWorkflowSortUpdate}
                handleEventNameFilterUpdate={handleEventNameFilterUpdate}
              />

              {!isLoading && workflowsWithRelationships.length > 0 && (
                <>
                  <WorkflowCardsView
                    isLoadingSchema={isLoadingSchema}
                    transactionTypes={transactionTypes}
                    workflowsWithRelationships={workflowsWithRelationships}
                    workflowSchema={workflowSchema}
                    setWorkflowToArchive={setWorkflowToArchive}
                    setWorkflowToActivate={setWorkflowToActivate}
                    setWorkflowToClone={setWorkflowToClone}
                    setWorkflowToDeactivate={setWorkflowToDeactivate}
                  />
                  <PaginationControlsCard>
                    <Pagination
                      pageSize={pageSize}
                      pageSizeOptions={[MIN_PAGE_SIZE, DEFAULT_PAGE_SIZE, 50]}
                      paginationParams={paginationParams}
                      handlePageSizeClick={handleWorkflowPageSizeClick}
                      handlePageLinkClick={handleWorkflowPageLinkClick}
                    />
                  </PaginationControlsCard>
                </>
              )}

              {!isLoading && workflowsWithRelationships.length === 0 && (
                <CenteredNullView>
                  <NullStateWorkflows />
                  <Spacer height={3} />
                  <div>Automate manual decisions and actions.</div>
                  <Spacer height={5} />
                  <a
                    href='https://support.withpersona.com/hc/en-us/articles/360041047533-Workflows'
                    target='_blank'
                    title='Learn more about Workflows (Opens in a new window)'
                    rel='noreferrer'
                  >
                    Learn more about Workflows.
                  </a>
                </CenteredNullView>
              )}

              {isLoading && (
                <Container>
                  <LoadingIndicator />
                </Container>
              )}
            </>
          )}
          {currentTab === 'modules' && (
            <>
              <ModulesViewConfigurationBar
                referencedWorkflowFilterOptions={referencedWorkflowFilterOptions}
                handleSortUpdate={(newSortParams) =>
                  workflowModulesTableState.setSort(newSortParams)
                }
                handleFilterUpdate={(newFilterParams) =>
                  workflowModulesTableState.setFilter((oldFilterParams) => ({
                    ...oldFilterParams,
                    ...newFilterParams,
                  }))
                }
                sortParams={workflowModulesTableState.sort}
                filters={workflowModulesTableState.filter}
              />
              {!isLoadingModules && workflowModulesWithRelationships.length > 0 && (
                <>
                  <ModuleCards
                    workflowModulesWithRelationships={workflowModulesWithRelationships}
                    setModuleToArchive={setModuleToArchive}
                    setModuleToActivate={setModuleToActivate}
                    setModuleToDeactivate={setModuleToDeactivate}
                  />
                  <PaginationControlsCard>
                    <Pagination
                      pageSize={workflowModulesTableState.pageSize}
                      pageSizeOptions={[MIN_PAGE_SIZE, DEFAULT_PAGE_SIZE, 50]}
                      paginationParams={workflowModulePaginationParams}
                      handlePageSizeClick={workflowModulesTableCallbacks.handlePageSizeClick}
                      handlePageLinkClick={workflowModulesTableCallbacks.handlePageLinkClick}
                    />
                  </PaginationControlsCard>
                </>
              )}

              {!isLoadingModules && workflowModulesWithRelationships.length === 0 && (
                <CenteredNullView>
                  <NullStateWorkflows />
                  <Spacer height={3} />
                  <div>Define and reuse subroutines</div>
                  <Spacer height={5} />
                  <a
                    // TODO(Ernest): point to module help page once it's up
                    href='https://support.withpersona.com/hc/en-us/articles/360041047533-Workflows'
                    target='_blank'
                    title='Learn more about Workflow Modules (Opens in a new window)'
                    rel='noreferrer'
                  >
                    Learn more about Workflow Modules.
                  </a>
                </CenteredNullView>
              )}

              {isLoadingModules && (
                <Container>
                  <LoadingIndicator />
                </Container>
              )}
            </>
          )}
        </Section>
      </Container>
    </>
  );
};

const WorkflowCards = styled.div`
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;

  & > div + div {
    margin-top: ${Spacing[4]};
  }
`;

interface WorkflowCardsViewProps {
  isLoadingSchema: boolean;
  setWorkflowToActivate: Dispatch<SetStateAction<Workflow | null>>;
  setWorkflowToArchive: Dispatch<SetStateAction<Workflow | null>>;
  setWorkflowToClone: Dispatch<SetStateAction<Workflow | null>>;
  setWorkflowToDeactivate: Dispatch<SetStateAction<Workflow | null>>;
  transactionTypes: TransactionType[];
  workflowSchema: WorkflowSchema;
  workflowsWithRelationships: {
    workflow: Workflow;
    workflowVersion: WorkflowVersion;
    workflowDeployment?: WorkflowDeployment;
    trigger: Trigger;
  }[];
}

const WorkflowCardsView = ({
  isLoadingSchema,
  setWorkflowToActivate,
  setWorkflowToArchive,
  setWorkflowToClone,
  setWorkflowToDeactivate,
  transactionTypes,
  workflowSchema,
  workflowsWithRelationships,
}: WorkflowCardsViewProps) => {
  return (
    <WorkflowCards>
      {workflowsWithRelationships.map(
        ({ workflow, workflowVersion, workflowDeployment, trigger }) => (
          <WorkflowCard
            key={workflowVersion.id}
            isLoadingSchema={isLoadingSchema}
            onArchiveWorkflow={setWorkflowToArchive}
            onRunWorkflow={setWorkflowToActivate}
            onCloneWorkflow={setWorkflowToClone}
            onPauseWorkflow={setWorkflowToDeactivate}
            workflow={workflow}
            workflowVersion={workflowVersion}
            workflowDeployment={workflowDeployment}
            trigger={trigger}
            workflowSchema={workflowSchema}
            transactionTypes={transactionTypes}
          />
        ),
      )}
    </WorkflowCards>
  );
};

export default DashboardWorkflowsView;
